import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Cookies from "js-cookie";
import { toast } from "sonner";
import { AxiosError } from "axios";

import {
  getAllEmployeesForSAQueryOptions,
  getEmployeesByAdminQueryOptions,
} from "@/config/queries/superadmin-query-options";
import {
  assignEmployeesToAdmin,
  unassignEmployees,
} from "@/config/mutation/super-admin";
import { Button } from "@/components/ui/button/button";
import { Badge } from "@/components/ui/badge/badge";

export const AdminManagedEmployees = ({ adminId }: { adminId: string }) => {
  const superAdminKey = Cookies.get("key") as string;
  const queryClient = useQueryClient();

  const [selectedUnassigned, setSelectedUnassigned] = useState<string[]>([]);
  const [selectedAssigned, setSelectedAssigned] = useState<string[]>([]);
  const [searchUnassigned, setSearchUnassigned] = useState("");
  const [searchAssigned, setSearchAssigned] = useState("");

  const { data: allEmployeesData, isPending: loadingAll } = useQuery(
    getAllEmployeesForSAQueryOptions(superAdminKey)
  );

  const { data: managedData, isPending: loadingManaged } = useQuery(
    getEmployeesByAdminQueryOptions(superAdminKey, adminId)
  );

  const assignMutation = useMutation({
    mutationFn: assignEmployeesToAdmin(superAdminKey),
  });

  const unassignMutation = useMutation({
    mutationFn: unassignEmployees(superAdminKey),
  });

  const managedIds = new Set(
    managedData?.employees?.map((e) => e._id) ?? []
  );

  const unassignedEmployees = (allEmployeesData?.employees ?? []).filter(
    (e) => !managedIds.has(e._id) && !e.blocked
  );

  const managedEmployees = managedData?.employees ?? [];

  const filteredUnassigned = unassignedEmployees.filter(
    (e) =>
      e.name?.toLowerCase().includes(searchUnassigned.toLowerCase()) ||
      e.email?.toLowerCase().includes(searchUnassigned.toLowerCase())
  );

  const filteredAssigned = managedEmployees.filter(
    (e) =>
      e.name?.toLowerCase().includes(searchAssigned.toLowerCase()) ||
      e.email?.toLowerCase().includes(searchAssigned.toLowerCase())
  );

  const handleAssign = () => {
    if (selectedUnassigned.length === 0) return;
    assignMutation.mutate(
      { adminId, employeeIds: selectedUnassigned },
      {
        onSuccess: () => {
          toast.success(`${selectedUnassigned.length} employee(s) assigned.`);
          setSelectedUnassigned([]);
          queryClient.invalidateQueries({ queryKey: ["sa-admin-employees", adminId] });
          queryClient.invalidateQueries({ queryKey: ["sa-employees"] });
        },
        onError: (error) => {
          const msg =
            error instanceof AxiosError
              ? error?.response?.data?.error
              : error?.message;
          toast.error(msg || "Failed to assign employees.");
        },
      }
    );
  };

  const handleUnassign = () => {
    if (selectedAssigned.length === 0) return;
    unassignMutation.mutate(
      { employeeIds: selectedAssigned },
      {
        onSuccess: () => {
          toast.success(`${selectedAssigned.length} employee(s) unassigned.`);
          setSelectedAssigned([]);
          queryClient.invalidateQueries({ queryKey: ["sa-admin-employees", adminId] });
          queryClient.invalidateQueries({ queryKey: ["sa-employees"] });
        },
        onError: (error) => {
          const msg =
            error instanceof AxiosError
              ? error?.response?.data?.error
              : error?.message;
          toast.error(msg || "Failed to unassign employees.");
        },
      }
    );
  };

  const toggleUnassigned = (id: string) => {
    setSelectedUnassigned((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleAssigned = (id: string) => {
    setSelectedAssigned((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  if (loadingAll || loadingManaged) {
    return <div className="p-4">Loading employees...</div>;
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Currently Managed */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-lg font-semibold">
            Managed Employees{" "}
            <Badge variant="secondary">{managedEmployees.length}</Badge>
          </h4>
          {selectedAssigned.length > 0 && (
            <Button
              variant="destructive"
              size="sm"
              onClick={handleUnassign}
              disabled={unassignMutation.isPending}
            >
              Unassign {selectedAssigned.length} selected
            </Button>
          )}
        </div>
        <input
          type="text"
          placeholder="Search managed employees..."
          value={searchAssigned}
          onChange={(e) => setSearchAssigned(e.target.value)}
          className="w-full mb-2 px-3 py-2 border rounded-md bg-background text-sm"
        />
        <div className="border rounded-md max-h-[300px] overflow-y-auto">
          {filteredAssigned.length === 0 ? (
            <div className="p-4 text-sm text-muted-foreground text-center">
              No employees assigned to this admin yet.
            </div>
          ) : (
            filteredAssigned.map((emp) => (
              <label
                key={emp._id}
                className="flex items-center gap-3 px-4 py-2 hover:bg-accent cursor-pointer border-b last:border-b-0"
              >
                <input
                  type="checkbox"
                  checked={selectedAssigned.includes(emp._id)}
                  onChange={() => toggleAssigned(emp._id)}
                  className="h-4 w-4"
                />
                <div className="flex-1">
                  <span className="font-medium">{emp.name}</span>
                  <span className="text-muted-foreground text-sm ml-2">
                    {emp.email}
                  </span>
                </div>
              </label>
            ))
          )}
        </div>
      </div>

      {/* Unassigned Employees */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-lg font-semibold">
            Unassigned Employees{" "}
            <Badge variant="secondary">{unassignedEmployees.length}</Badge>
          </h4>
          {selectedUnassigned.length > 0 && (
            <Button
              size="sm"
              onClick={handleAssign}
              disabled={assignMutation.isPending}
            >
              Assign {selectedUnassigned.length} selected
            </Button>
          )}
        </div>
        <input
          type="text"
          placeholder="Search unassigned employees..."
          value={searchUnassigned}
          onChange={(e) => setSearchUnassigned(e.target.value)}
          className="w-full mb-2 px-3 py-2 border rounded-md bg-background text-sm"
        />
        <div className="border rounded-md max-h-[300px] overflow-y-auto">
          {filteredUnassigned.length === 0 ? (
            <div className="p-4 text-sm text-muted-foreground text-center">
              All employees are assigned.
            </div>
          ) : (
            filteredUnassigned.map((emp) => (
              <label
                key={emp._id}
                className="flex items-center gap-3 px-4 py-2 hover:bg-accent cursor-pointer border-b last:border-b-0"
              >
                <input
                  type="checkbox"
                  checked={selectedUnassigned.includes(emp._id)}
                  onChange={() => toggleUnassigned(emp._id)}
                  className="h-4 w-4"
                />
                <div className="flex-1">
                  <span className="font-medium">{emp.name}</span>
                  <span className="text-muted-foreground text-sm ml-2">
                    {emp.email}
                  </span>
                </div>
              </label>
            ))
          )}
        </div>
      </div>
    </div>
  );
};
